import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { apiGet } from '@/lib/api';
import { formatBps, formatDate, formatKop } from '@/lib/format';
import { useAuthStore } from '@/store/auth.store';

interface Profile {
  name: string;
  code: string;
  commissionBps: number;
  referralLink: string;
  stats: { establishments: number; earnedKopecks: number; pendingKopecks: number };
}

interface RecentPayment {
  id: string;
  establishmentId: string | null;
  establishmentName: string;
  kind: 'COMMISSION' | 'STORNO';
  paymentKopecks: number;
  bps: number;
  amountKopecks: number;
  paidAt: string;
  paidOut: boolean;
}

interface Summary {
  pendingKopecks: number;
  byMonth: Array<{ month: string; amountKopecks: number; accruals: number }>;
  recentPayments: RecentPayment[];
}

const MONTH_FMT = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' });

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  // Заглавная только у первой буквы: CSS-capitalize поднял бы и «г.» в «Г.».
  const s = MONTH_FMT.format(new Date(y, m - 1, 1));
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function Dashboard() {
  const manager = useAuthStore((s) => s.manager);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([apiGet<Profile>('/me'), apiGet<Summary>('/summary')])
      .then(([p, s]) => {
        if (cancelled) return;
        setProfile(p);
        setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить данные');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copyLink = () => {
    if (!profile?.referralLink) return;
    void navigator.clipboard.writeText(profile.referralLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="text-ink/50 text-sm">Загрузка…</div>;
  if (error) return <div className="card p-4 text-sm text-ink/70">{error}</div>;

  const recent = summary?.recentPayments ?? [];

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">Привет, {manager?.name ?? 'менеджер'}</h1>
        <p className="text-ink/55 text-sm mt-1">
          Ставка {profile ? formatBps(profile.commissionBps) : '—'} · код {profile?.code ?? '—'}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <div className="text-xs text-ink/50">К выплате</div>
          <div className="text-2xl font-semibold mt-1">
            {formatKop(summary?.pendingKopecks ?? profile?.stats.pendingKopecks ?? 0)}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-ink/50">Заведений</div>
          <div className="text-2xl font-semibold mt-1">{profile?.stats.establishments ?? 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-ink/50">Начислено всего</div>
          <div className="text-2xl font-semibold mt-1">
            {formatKop(profile?.stats.earnedKopecks ?? 0)}
          </div>
        </div>
      </div>

      {/* Кто оплатил — главное, что менеджер хочет видеть на входе. */}
      <div className="card overflow-hidden">
        <div className="border-b border-line px-4 py-3 text-sm font-medium">Последние оплаты</div>
        {recent.length === 0 ? (
          <div className="px-4 py-6 text-sm text-ink/50">
            Пока никто из ваших заведений не оплачивал подписку.
          </div>
        ) : (
          <ul className="divide-y divide-line/60">
            {recent.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.establishmentName}</div>
                  <div className="mt-0.5 text-xs text-ink/50">
                    {formatDate(p.paidAt)} · подписка {formatKop(p.paymentKopecks)} ·{' '}
                    {formatBps(p.bps)}
                    {p.kind === 'STORNO' ? ' · возврат' : ''}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div
                    className={`font-semibold ${p.kind === 'STORNO' ? 'text-rose-600' : 'text-emerald-600'}`}
                  >
                    {p.kind === 'STORNO' ? '' : '+'}
                    {formatKop(p.amountKopecks)}
                  </div>
                  <div className="text-[11px] text-ink/40">
                    {p.paidOut ? 'выплачено' : 'к выплате'}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {profile?.referralLink ? (
        <div className="card p-4">
          <div className="text-sm font-medium mb-2">Реферальная ссылка</div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input className="input text-xs" readOnly value={profile.referralLink} />
            <button type="button" className="btn-primary w-full shrink-0 sm:w-auto" onClick={copyLink}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Скопировано' : 'Копировать'}
            </button>
          </div>
        </div>
      ) : null}

      {summary?.byMonth.length ? (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-line font-medium text-sm">По месяцам</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink/50 border-b border-line">
                <th className="px-4 py-2">Месяц</th>
                <th className="px-4 py-2">Начислений</th>
                <th className="px-4 py-2 text-right">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {summary.byMonth.map((row) => (
                <tr key={row.month} className="border-b border-line/60 last:border-0">
                  <td className="px-4 py-2">{monthLabel(row.month)}</td>
                  <td className="px-4 py-2">{row.accruals}</td>
                  <td className="px-4 py-2 text-right font-medium">{formatKop(row.amountKopecks)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
